// Session state for the whole app.
//
// The backend is the security boundary — every endpoint checks the user's
// Anixify role itself. Nothing here is a gate; it decides what to RENDER,
// and a bug in it can only ever show a control that the server will then
// refuse. Never move an access decision into this file.
//
// The session cookie is HttpOnly, so this context cannot read it. "Am I
// signed in?" is answered by asking the server (/anixify/whoami), which is
// also the only answer that stays true when a session expires server-side.

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";
import { auth, type SessionUser } from "@/services/api";
import { hasUnsavedWork, onSessionExpired } from "@/lib/sessionExpiry";
import { colors, semanticColors, radius, spacing, heights, layout, typography, shadows } from "@/styles/tokens";

/** What the sign-in screen and the warning say when a session ends under an
 *  open page (session-expired-redirect, owner 2026-10-06). */
export const SESSION_EXPIRED_TEXT = "Your session has expired — please sign in again";

type Status = "checking" | "in" | "out";

interface AuthValue {
  status: Status;
  user: SessionUser | null;
  role: SessionUser["role"];
  signIn: (login: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  signOut: () => Promise<void>;
  /** Re-ask the server who we are. Used when a call comes back 401. */
  refresh: () => Promise<void>;
  /** True when the user was sent to sign-in because a call came back 401. */
  sessionExpired: boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // "checking" until the first whoami answers. Rendering the sign-in screen
  // during that window would flash a login form at an already-signed-in
  // user on every page load.
  const [status, setStatus] = useState<Status>("checking");
  const [user, setUser] = useState<SessionUser | null>(null);

  const refresh = useCallback(async () => {
    const found = await auth.whoami();
    setUser(found);
    setStatus(found ? "in" : "out");
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // SESSION EXPIRED UNDER AN OPEN PAGE (session-expired-redirect). Any
  // backend call answering 401 lands here. Signed in -> the sign-in screen,
  // with the URL left alone so signing in again returns to the same page.
  // Unsaved work on screen -> warn first; "Stay on this page" keeps it
  // visible (with a banner) so nothing is lost without the user choosing.
  const [sessionExpired, setSessionExpired] = useState(false);
  const [warnUnsaved, setWarnUnsaved] = useState(false);
  const [stayed, setStayed] = useState(false);
  const statusRef = useRef<Status>("checking");
  statusRef.current = status;
  const goToSignIn = useCallback(() => {
    setWarnUnsaved(false);
    setStayed(false);
    setSessionExpired(true);
    setUser(null);
    setStatus("out");
  }, []);
  useEffect(() => onSessionExpired(() => {
    if (statusRef.current !== "in") return;
    if (hasUnsavedWork()) { setWarnUnsaved(true); return; }
    goToSignIn();
  }), [goToSignIn]);

  const signIn = useCallback(async (login: string, password: string) => {
    const result = await auth.login(login, password);
    if (!result.ok || !result.user) {
      return { ok: false, error: result.error };
    }
    setSessionExpired(false);
    setUser(result.user);
    setStatus("in");
    return { ok: true };
  }, []);

  const signOut = useCallback(async () => {
    await auth.logout();
    setSessionExpired(false);
    setUser(null);
    setStatus("out");
  }, []);

  const value = useMemo<AuthValue>(
    () => ({ status, user, role: user?.role ?? null, signIn, signOut, refresh, sessionExpired }),
    [status, user, signIn, signOut, refresh, sessionExpired],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      {status === "in" && stayed && !warnUnsaved && (
        <div data-testid="session-expired-banner" role="alert" style={{
          position: "fixed", left: "50%", bottom: spacing[16], transform: "translateX(-50%)", zIndex: 1100,
          display: "flex", alignItems: "center", gap: spacing[12], padding: `${spacing[8]}px ${spacing[12]}px`,
          backgroundColor: semanticColors.warningBg, border: `1px solid ${semanticColors.warningBorder}`,
          borderRadius: radius.base, boxShadow: shadows.modal, color: colors.amber800,
          fontSize: typography.fontSize.sm, maxWidth: "calc(100vw - 32px)",
        }}>
          <span>{SESSION_EXPIRED_TEXT}. Nothing here can be saved until you do.</span>
          <button data-testid="session-expired-banner-signin" onClick={goToSignIn} style={EXPIRY_PRIMARY}>
            Sign in again
          </button>
        </div>
      )}
      {status === "in" && warnUnsaved && (
        <div data-testid="session-expired-dialog" role="dialog" aria-modal="true"
          aria-labelledby="session-expired-title" style={{
            position: "fixed", inset: 0, zIndex: 1200, backgroundColor: semanticColors.overlay,
            display: "flex", alignItems: "center", justifyContent: "center", padding: spacing[16],
          }}>
          <div style={{
            width: layout.modalWidth, maxWidth: "100%", backgroundColor: semanticColors.surface,
            border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
            boxShadow: shadows.modal, padding: spacing[20],
          }}>
            <div id="session-expired-title" data-testid="session-expired-text" style={{
              fontSize: typography.fontSize.base, color: semanticColors.textPrimary,
              lineHeight: typography.lineHeight.base,
            }}>
              <strong>{SESSION_EXPIRED_TEXT}.</strong> You have unsaved changes. Signing in again
              reloads this page from what was last saved, so those changes will be lost.
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: spacing[8], marginTop: spacing[20] }}>
              <button data-testid="session-expired-stay" style={EXPIRY_SECONDARY}
                onClick={() => { setWarnUnsaved(false); setStayed(true); }}>
                Stay on this page
              </button>
              <button data-testid="session-expired-signin" style={EXPIRY_PRIMARY} onClick={goToSignIn} autoFocus>
                Sign in again
              </button>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}

const EXPIRY_SECONDARY: React.CSSProperties = {
  height: heights.control, padding: `0 ${spacing[14]}px`, backgroundColor: semanticColors.surface,
  color: semanticColors.textSecondary, border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium, cursor: "pointer",
};
const EXPIRY_PRIMARY: React.CSSProperties = {
  ...EXPIRY_SECONDARY, backgroundColor: colors.indigo600, color: semanticColors.onAccent, border: "none",
};

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/** The signed-in user's id, or null outside a provider / signed out. Used to
 *  avoid OFFERING an action on your own row that the server would refuse. */
export function useSessionUidOrNull(): number | null {
  const ctx = useContext(AuthContext);
  return ctx?.user?.uid ?? null;
}

// ---- Cosmetic capability helpers ----------------------------------------
//
// COSMETIC ONLY. These hide controls a user cannot use, so the UI does not
// offer an action that will be refused. They are NOT access control: the
// backend already denies the same actions, and it is what actually stops
// them. A hidden button is a courtesy, not a lock.

/** May price and save quotes. Sales and Admin. */
export function useCanEditQuotes(): boolean {
  const { role } = useAuth();
  return role === "sales" || role === "admin";
}

/** Is the signed-in user an Admin, and who are they? For the quote list's
 *  team-drafts read.
 *
 *  ⚠️ DOES NOT THROW OUTSIDE <AuthProvider>, unlike useAuth: outside one it
 *  answers "not an Admin", which only ever means asking the server for LESS.
 *  Cosmetic like everything in this section -- /team_drafts refuses anyone but
 *  an Admin 403 whatever this returns. */
export function useAdminView(): { isAdmin: boolean; uid: number | undefined } {
  const ctx = useContext(AuthContext);
  return { isAdmin: ctx?.role === "admin", uid: ctx?.user?.uid };
}

/** May save company settings. Admin only. */
/** Is the signed-in user an Admin? FAILS CLOSED: outside an AuthProvider it
 *  answers false instead of throwing.
 *
 *  ⚠️ WHY A SECOND, NON-THROWING READ EXISTS. The Exchange Rates publish form
 *  sits on Settings' DEFAULT tab, so its role check runs on every mount of
 *  the page -- including in suites that render <SettingsPage /> bare, where
 *  useAuth throws. "No session context" must mean "not an Admin", never
 *  "crash" and never "allowed"; the route refuses non-Admins with a 403
 *  whatever this answers, so failing closed here costs nothing. */
export function useIsAdminOrFalse(): boolean {
  return useContext(AuthContext)?.role === "admin";
}

/** May edit an existing customer's billing details? Sales and Admin.
 *  FAILS CLOSED outside an AuthProvider, like useIsAdminOrFalse: the quote
 *  screen renders bare in many suites, and "no session context" must mean
 *  "no edit button", never a crash. Cosmetic -- /update_customer refuses a
 *  Viewer with a 403 whatever this answers. */
export function useCanEditCustomersOrFalse(): boolean {
  const role = useContext(AuthContext)?.role;
  return role === "sales" || role === "admin";
}

/** May see the sales report? Sales and Admin. FAILS CLOSED outside an
 *  AuthProvider. Cosmetic: /sales_report refuses a Viewer with a 403. */
export function useCanSeeReportsOrFalse(): boolean {
  const role = useContext(AuthContext)?.role;
  return role === "sales" || role === "admin";
}

/** May see the follow-up list? Sales and Admin (follow-up-list). FAILS
 *  CLOSED outside an AuthProvider -- the quote list renders bare in many
 *  suites. Cosmetic: /follow_up refuses a Viewer with a 403. */
export function useCanSeeFollowUpOrFalse(): boolean {
  const role = useContext(AuthContext)?.role;
  return role === "sales" || role === "admin";
}

export function useCanEditSettings(): boolean {
  return useAuth().role === "admin";
}

/** May edit THIS quote: own-only, Admin-any.
 *
 * COSMETIC, like the rest of this section. The backend refuses an update to
 * someone else's quote with a 403 whatever this returns; hiding the controls
 * only avoids offering an action that would fail.
 *
 * `ownerUid === undefined` means the quote has never been persisted -- a
 * local mock the user is building -- so there is no owner to check yet and
 * editing is allowed. `null` means it IS persisted but unowned (saved before
 * ownership existed); the backend refuses those to non-Admins, so the UI
 * matches rather than offering a button that 403s.
 */
export function useCanEditQuote(ownerUid: number | null | undefined): boolean {
  const { role, user } = useAuth();
  const canEditAtAll = role === "sales" || role === "admin";
  if (!canEditAtAll) return false;
  if (role === "admin") return true;
  if (ownerUid === undefined) return true;
  return ownerUid !== null && ownerUid === user?.uid;
}
